import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getTransactions } from '../services/transactionService';
import { getAccounts } from '../services/accountService';
import { getBudgets } from '../services/budgetService';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import './Dashboard.css';

const Dashboard = () => {
  const { currentUser, logout } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    loadData();
  }, [currentUser]);

  const loadData = async () => {
    if (currentUser) {
      const txs = await getTransactions(currentUser.uid);
      const accs = await getAccounts(currentUser.uid);
      const buds = await getBudgets(currentUser.uid);
      setTransactions(txs);
      setAccounts(accs);
      setBudgets(buds);
      setLoading(false);
    }
  };

  const getMonthlyTransactions = () => {
    return transactions.filter(t => {
      const txDate = t.date.slice(0, 7); // YYYY-MM
      return txDate === selectedMonth;
    });
  };

  const calculateTotals = () => {
    const monthlyTxs = getMonthlyTransactions();
    
    const income = monthlyTxs
      .filter(t => t.type === 'ingreso')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const expenses = monthlyTxs
      .filter(t => t.type === 'egreso')
      .reduce((sum, t) => sum + t.amount, 0);
    
    // Sumar el balance de todas las cuentas
    const accountsBalance = accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
    
    const totalBalance = accountsBalance;
    
    return { income, expenses, balance: totalBalance, accountsBalance };
  };

  const getCategoryData = () => {
    const monthlyTxs = getMonthlyTransactions();
    const categories = {};
    monthlyTxs
      .filter(t => t.type === 'egreso')
      .forEach(t => {
        categories[t.category] = (categories[t.category] || 0) + t.amount;
      });
    
    return Object.entries(categories).map(([name, value]) => ({ name, value }));
  };

  const getBudgetProgress = () => {
    const monthlyTxs = getMonthlyTransactions();
    
    return budgets.map(budget => {
      // Calcular gastos de la categoría en el mes
      const spent = monthlyTxs
        .filter(t => t.type === 'egreso' && t.category === budget.category)
        .reduce((sum, t) => sum + t.amount, 0);
      
      const remaining = budget.amount - spent;
      const percentage = (spent / budget.amount) * 100;
      
      return {
        ...budget,
        spent,
        remaining: remaining > 0 ? remaining : 0,
        percentage: percentage > 100 ? 100 : percentage,
        isOverBudget: spent > budget.amount
      };
    });
  };

  const getMonthName = (monthStr) => {
    const date = new Date(monthStr + '-01');
    return date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  };

  const totals = calculateTotals();
  const categoryData = getCategoryData();
  const budgetProgress = getBudgetProgress();
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

  if (loading) return <div className="loading">Cargando...</div>;

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>💰 Dashboard</h1>
        <button onClick={logout} className="logout-btn">Cerrar Sesión</button>
      </header>

      <div className="month-selector">
        <label>Ver mes:</label>
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          max={new Date().toISOString().slice(0, 7)}
        />
        <span className="month-name">{getMonthName(selectedMonth)}</span>
      </div>

      <div className="summary-cards">
        <div className="card income">
          <h3>Ingresos del Mes</h3>
          <p className="amount">${totals.income.toFixed(2)}</p>
          <span className="card-subtitle">{getMonthName(selectedMonth)}</span>
        </div>
        <div className="card expenses">
          <h3>Egresos del Mes</h3>
          <p className="amount">${totals.expenses.toFixed(2)}</p>
          <span className="card-subtitle">{getMonthName(selectedMonth)}</span>
        </div>
        <div className="card balance">
          <h3>Balance Total</h3>
          <p className="amount">${totals.balance.toFixed(2)}</p>
          <span className="card-subtitle">Saldo actual en cuentas</span>
        </div>
      </div>

      {accounts.length > 0 && (
        <div className="accounts-summary">
          <h2>Resumen de Cuentas</h2>
          <div className="accounts-grid">
            {accounts.map(account => (
              <div key={account.id} className="account-summary-card">
                <div className="account-name">{account.name}</div>
                <div className="account-type-badge">{account.type}</div>
                <div className="account-balance-display">
                  ${account.balance?.toFixed(2) || '0.00'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {budgetProgress.length > 0 && (
        <div className="budget-progress-container">
          <h2>Progreso de Presupuestos - {getMonthName(selectedMonth)}</h2>
          <div className="budget-progress-list">
            {budgetProgress.map((budget, index) => (
              <div key={budget.id || index} className="budget-progress-item">
                <div className="budget-progress-header">
                  <span className="budget-category">{budget.category}</span>
                  <span className="budget-amounts">
                    <span className={budget.isOverBudget ? 'over-budget' : 'spent'}>
                      ${budget.spent.toFixed(2)}
                    </span>
                    {' / '}
                    <span className="total">${budget.amount.toFixed(2)}</span>
                  </span>
                </div>
                <div className="progress-bar-container">
                  <div 
                    className={`progress-bar ${budget.isOverBudget ? 'over-budget' : ''}`}
                    style={{ width: `${budget.percentage}%` }}
                  ></div>
                </div>
                <div className="budget-progress-footer">
                  <span className={budget.isOverBudget ? 'over-budget-text' : 'remaining-text'}>
                    {budget.isOverBudget 
                      ? `Excedido por $${(budget.spent - budget.amount).toFixed(2)}`
                      : `Disponible: $${budget.remaining.toFixed(2)}`
                    }
                  </span>
                  <span className="percentage">{budget.percentage.toFixed(0)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {categoryData.length > 0 && (
        <div className="chart-container">
          <h2>Gastos por Categoría - {getMonthName(selectedMonth)}</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="quick-actions">
        <a href="/transactions" className="action-btn">📝 Transacciones</a>
        <a href="/accounts" className="action-btn">🏦 Cuentas</a>
        <a href="/budgets" className="action-btn">📊 Presupuestos</a>
        <a href="/calendar" className="action-btn">📅 Calendario</a>
        <a href="/categories" className="action-btn">🏷️ Categorías</a>
      </div>
    </div>
  );
};

export default Dashboard;
